import dayjs from "dayjs";
import type * as MaterialLocales from "@mui/material/locale";
import type * as DataGridLocales from "@mui/x-data-grid-premium/locales";
import type * as DatePickersLocales from "@mui/x-date-pickers/locales";

type MaterialThemeLocale = MaterialLocales.Localization;
type DataGridThemeLocale = (typeof DataGridLocales)["enUS"];
type DatePickersThemeLocale = (typeof DatePickersLocales)["enUS"];

export type MuiThemeLocaleFragment =
  MaterialThemeLocale | DataGridThemeLocale | DatePickersThemeLocale;

/**
 * **Identity / i18n** use **BCP 47** (Trimble documents those codes
 * https://docs.trimblecloud.com/trimble-identity/content/reference/tid-internationalization/).
 * That is the authoritative list of which strings the app may receive.
 *
 * **MUI** still describes the same ISO **language + region** idea, but locale packs
 * are looked up by **camelCase export names** (`jaJP`, `enUS`) — valid JS
 * identifiers — not by hyphenated BCP 47 strings (`ja-JP`). Same subtags, different
 * delimiter / casing shape for the key string.
 *
 * **This table is not a second copy of Trimble’s language list.** It is only used
 * when the incoming tag is **language-only** (no region), so there is nothing to
 * concatenate after the language code. We then pick a **default ISO 3166-1
 * region** (common “primary” territory for that language in many products) so the
 * result matches a real MUI export (`ja` → `jaJP`). Choices like `pt` → Brazil vs
 * Portugal are **heuristics for MUI lookup**, not claims about what Trimble meant
 * for ambiguous `pt`; prefer sending `pt-BR` / `pt-PT` from identity when it matters.
 *
 * For **language-region** tags (`en-US`, `es-AR`), {@link toMuiLocaleKey} does not
 * use this table; it mechanically folds subtags to camelCase (`esAR`). MUI does
 * **not** ship every BCP 47 region — e.g. `@mui/material/locale` has `esES` but not
 * `esAR` — so some valid identity codes still won’t load a Material pack; that is a
 * MUI catalog limitation, not a rejection of BCP 47 on the wire.
 */
const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  ja: "JP",
  de: "DE",
  fr: "FR",
  es: "ES",
  it: "IT",
  ko: "KR",
  zh: "CN",
  pt: "BR",
  nl: "NL",
  pl: "PL",
  ru: "RU",
  tr: "TR",
  uk: "UA",
  vi: "VN",
  th: "TH", // → `thTH`: in `@mui/material/locale` only; not in x-data-grid / x-date-pickers barrels
  ar: "SA", // → `arSA`: Material only; Grid/Pickers omit `arSA` (Arabic not in those locale indexes)
  he: "IL",
  hi: "IN", // → `hiIN`: Material only; not in x-data-grid / x-date-pickers barrels
  sv: "SE",
  nb: "NO",
  da: "DK",
  fi: "FI",
  cs: "CZ",
  ro: "RO",
  hu: "HU",
  el: "GR",
  sk: "SK",
  bg: "BG",
  hr: "HR",
  sl: "SI", // → `slSI`: not shipped by Material, x-data-grid, or x-date-pickers locales
};

/**
 * Maps a **BCP 47-style** locale string to the **MUI locale export key** (camelCase
 * `lang` + `region`, no hyphen). The first `-` splits language vs region; tags with
 * more segments (e.g. `zh-Hans-CN`) are not fully parsed — use a simple `xx-YY`
 * shape when possible.
 *
 * Hyphenated tags bypass {@link LANGUAGE_TO_COUNTRY}; bare language codes use it.
 *
 * @example "en-US" → "enUS", "ja" → "jaJP", "pt-BR" → "ptBR", "es-AR" → "esAR"
 */
export function toMuiLocaleKey(locale: string) {
  const [langPart, countryPart] = locale.split("-");
  if (langPart && countryPart) {
    return langPart.toLowerCase() + countryPart.toUpperCase();
  }
  const lang = (langPart ?? locale).toLowerCase();
  const country = LANGUAGE_TO_COUNTRY[lang];
  return country ? lang + country : `${lang}${lang.toUpperCase()}`;
}

/**
 * Keys {@link toMuiLocaleKey} produces that have **no** matching export in the MUI
 * locale barrels we load, mapped to the closest pack we **do** ship.
 *
 * - **`esAR` → `esES`:** Trimble offers Latin American Spanish (`es-AR`), but
 *   `@mui/material` / `@mui/x-data-grid` / `@mui/x-date-pickers` only publish **`esES`**
 *   for Spanish component strings. Using Spain’s pack is imperfect for LA users but
 *   better than leaving MUI chrome in English. App copy stays on whatever i18n tag
 *   you use (`es-AR`, etc.).
 */
const MUI_THEME_LOCALE_ALIASES: Record<string, string> = {
  esAR: "esES",
};

/**
 * Dynamically loads MUI locale packs for @mui/material, @mui/x-data-grid-premium,
 * and @mui/x-date-pickers. Returns an array of locale objects ready to be spread
 * into createTheme().
 */
export async function getMuiLocales(locale: string) {
  const rawKey = toMuiLocaleKey(locale);
  const key = MUI_THEME_LOCALE_ALIASES[rawKey] ?? rawKey;
  if (key === "enUS") return [];

  const lookup = (m: Record<string, unknown>) => m[key];

  const results = await Promise.allSettled([
    import("@mui/material/locale").then(lookup),
    import("@mui/x-data-grid-premium/locales").then(lookup),
    import("@mui/x-date-pickers/locales").then(lookup),
  ]);

  return results
    .filter(
      (r): r is PromiseFulfilledResult<MuiThemeLocaleFragment> =>
        r.status === "fulfilled" && r.value != null
    )
    .map((r) => r.value);
}

const dayjsLocaleLoaders = import.meta.glob<{ default: ILocale }>(
  "/node_modules/dayjs/esm/locale/*.js"
);

function hasDayjsLocale(name: string) {
  return `/node_modules/dayjs/esm/locale/${name}.js` in dayjsLocaleLoaders;
}

/**
 * Loads and registers a dayjs locale on the app's dayjs instance.
 *
 * dayjs ships two builds (`dayjs.min.js` CJS and `esm/index.js`) with no
 * `"module"` or `"exports"` field, so `import dayjs from "dayjs"` (CJS)
 * and the ESM locale's `import dayjs from '../index'` resolve to different
 * instances. The locale file's self-registration therefore targets the wrong
 * one. We load the ESM file (which won't crash unlike UMD), grab the config,
 * and register it ourselves on the app's dayjs.
 *
 * @returns `true` if the locale was loaded and registered, `false` if no file exists.
 */
export async function loadDayjsLocale(name: string) {
  if (name === "en") return true;

  const loader =
    dayjsLocaleLoaders[`/node_modules/dayjs/esm/locale/${name}.js`];
  if (!loader) return false;

  const { default: localeConfig } = await loader();
  dayjs.locale(localeConfig, undefined, true);
  return true;
}

/**
 * Resolves an i18n locale string to the actual dayjs locale name.
 * dayjs locale files don't always include the region (e.g. `ja` exists
 * but `ja-jp` does not), so we fall back to the base language code.
 */
export function toDayjsLocale(locale: string) {
  const normalized = locale.toLowerCase();
  if (normalized === "en" || normalized === "en-us") return "en";

  if (hasDayjsLocale(normalized)) return normalized;

  const [base] = normalized.split("-");
  if (base && hasDayjsLocale(base)) return base;

  return "en";
}
